import React, { useState } from "react";
import { useSelector } from "react-redux";
import SubmitButton from "../submitButton/SubmitButton";
import { useNotifications } from "../../context/Notifications";
import { CountrySelect } from "react-country-state-city";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";
import { useTranslator } from "context/translator";
import {
  createCampaignListService,
  updateCampaignListService,
} from "services/campaignList/services";

function CreateCustomerListModal({
  customersList,
  buttonName,
  getCustomerLists,
  categories,
}) {
  const { translate } = useTranslator();
  const { user } = useSelector((state) => state.user);

  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    { skip: !userProfile?.user?.companyId },
  );

  const defaultState = {
    name: "",
    withEmail: true,
    withPhone: true,
    category: "ALL",
    country: { id: 0, name: "No Selected" },
    status: "ACTIVE",
    companyId: company?._id,
    allCustomer: true,
  };

  const [temporalCustomersList, setTemporalCustomersList] =
    useState(customersList);
  const { addNotification } = useNotifications();
  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const toggleModal = () => setModalOpen((prev) => !prev);

  const handleOnChangeAddress = (name, value) => {
    setTemporalCustomersList((prev) => ({ ...prev, [name]: value }));
  };

  const handleOnChange = (event) => {
    const { name, value } = event.target;
    setTemporalCustomersList((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    setTemporalCustomersList({ ...defaultState, companyId: company?._id });
    toggleModal();
  };

  const handleCreateCustomerList = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsLoading(true);

    let response = null;
    if (temporalCustomersList._id) {
      response = await updateCampaignListService({
        data: { ...temporalCustomersList },
        campaignListId: temporalCustomersList._id,
      });
    } else {
      response = await createCampaignListService({
        data: { ...temporalCustomersList, companyId: company?._id },
      });
    }

    if (response?.campaignList) {
      addNotification({
        id: Date.now(),
        message: temporalCustomersList?._id
          ? "Update Campaign List!"
          : "New Campaign List!",
        kind: "success",
      });
      await getCustomerLists();
      setTemporalCustomersList({ ...defaultState, companyId: company?._id });
      toggleModal();
    } else {
      addNotification({
        id: Date.now(),
        message: temporalCustomersList?._id
          ? "Error updating customer list!"
          : "Error creating customer list!",
        kind: "danger",
      });
    }

    setIsLoading(false);
  };

  if (userLoading || companyLoading) return null;

  const isAllCustomer = temporalCustomersList.allCustomer;

  return (
    <>
      {/* ── Trigger Button ── */}
      <button
        type="button"
        onClick={toggleModal}
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold rounded-xl shadow-md shadow-indigo-500/30 transition-all duration-150 hover:-translate-y-px hover:shadow-lg hover:shadow-indigo-500/40 active:translate-y-0"
      >
        <svg
          width="16"
          height="16"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 4v16m8-8H4"
          />
        </svg>
        {translate(buttonName || "new-list")}
      </button>

      {/* ── Modal Overlay ── */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/55 backdrop-blur-sm"
          onClick={(e) => e.target === e.currentTarget && handleCancel()}
        >
          <div className="flex w-full max-w-2xl max-h-[90vh] rounded-2xl overflow-hidden shadow-2xl shadow-slate-900/20 dark:shadow-slate-900/60">
            {/* ── Left Panel ── */}
            <div className="w-56 flex-shrink-0 bg-blue-700 dark:bg-blue-800 px-6 py-10 flex flex-col justify-between text-white">
              <div>
                {/* Icon */}
                <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center mb-5">
                  <svg
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="white"
                    strokeWidth={1.8}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17 20h5v-2a4 4 0 00-4-4h-1M9 20H4v-2a4 4 0 014-4h1m4-4a4 4 0 110-8 4 4 0 010 8zm6 4a2 2 0 100-4 2 2 0 000 4zM3 18a2 2 0 100-4 2 2 0 000 4z"
                    />
                  </svg>
                </div>

                <p className="text-lg font-bold leading-snug mb-2.5">
                  {translate(buttonName || "new-list")}
                </p>
                <p className="text-xs opacity-80 leading-relaxed">
                  Define a customer segment to target in your next campaign.
                </p>
              </div>

              {/* Feature list */}
              <div className="text-xs opacity-65 space-y-1.5">
                {[
                  "Filter by country",
                  "Filter by category",
                  "Choose contact channels",
                ].map((feat) => (
                  <div key={feat} className="flex items-center gap-1.5">
                    <svg
                      width="14"
                      height="14"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="white"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {feat}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Right Panel ── */}
            <div className="flex-1 overflow-y-auto bg-white dark:bg-gray-900 px-7 py-8">
              {/* Header */}
              <div className="flex items-center justify-between mb-7">
                <div>
                  <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wider mb-1">
                    Campaign Setup
                  </p>
                  <h3 className="text-xl font-bold text-slate-800 dark:text-gray-100 m-0">
                    Configure your list
                  </h3>
                </div>

                {/* Close button */}
                <button
                  type="button"
                  onClick={handleCancel}
                  className="w-9 h-9 flex items-center justify-center rounded-xl border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800 text-slate-500 dark:text-gray-400 hover:bg-slate-200 dark:hover:bg-gray-700 hover:text-slate-700 dark:hover:text-gray-200 transition-colors duration-150"
                >
                  <svg
                    width="16"
                    height="16"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleCreateCustomerList}>
                {/* ── All Customers Toggle ── */}
                <div
                  className={`flex items-center justify-between p-4 rounded-xl border-[1.5px] mb-6 cursor-pointer transition-all duration-200
                    ${
                      isAllCustomer
                        ? "bg-indigo-50/70 border-blue-700 dark:bg-blue-900/20 dark:border-blue-500"
                        : "bg-slate-50 border-slate-200 dark:bg-gray-800 dark:border-gray-700"
                    }`}
                  onClick={() =>
                    setTemporalCustomersList((prev) => ({
                      ...prev,
                      allCustomer: !prev.allCustomer,
                      category: !prev.allCustomer ? "ALL" : prev.category,
                      withEmail: !prev.allCustomer ? true : prev.withEmail,
                      withPhone: !prev.allCustomer ? true : prev.withPhone,
                    }))
                  }
                >
                  <div>
                    <p className="text-sm font-semibold text-slate-800 dark:text-gray-100 m-0">
                      {translate("all-customers")}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5 mb-0">
                      Include every customer in your database
                    </p>
                  </div>

                  {/* Toggle switch */}
                  <div
                    className={`relative w-11 h-6 rounded-full flex-shrink-0 transition-colors duration-200
                      ${isAllCustomer ? "bg-blue-700 dark:bg-blue-600" : "bg-slate-300 dark:bg-gray-600"}`}
                  >
                    <div
                      className={`absolute top-[3px] w-[18px] h-[18px] bg-white rounded-full shadow transition-all duration-200
                        ${isAllCustomer ? "left-[23px]" : "left-[3px]"}`}
                    />
                  </div>
                </div>

                {/* ── Name & Country ── */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block mb-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300">
                      {translate("name")}
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={temporalCustomersList.name}
                      onChange={handleOnChange}
                      placeholder="e.g. VIP Customers"
                      required
                      className="w-full px-3 py-2 text-sm border-[1.5px] border-slate-200 dark:border-gray-700 rounded-xl outline-none bg-white dark:bg-gray-800 text-slate-800 dark:text-gray-100 placeholder-slate-400 dark:placeholder-gray-500 focus:border-blue-700 dark:focus:border-blue-500 focus:ring-2 focus:ring-blue-700/10 dark:focus:ring-blue-500/20 transition-all"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-1.5 mb-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300">
                      {translate("country")}
                      {isAllCustomer && (
                        <span className="text-[11px] bg-slate-200 dark:bg-gray-700 text-slate-500 dark:text-gray-400 px-2 py-0.5 rounded-full font-medium">
                          All
                        </span>
                      )}
                    </label>
                    <div
                      className={`transition-opacity duration-200 ${isAllCustomer ? "opacity-45" : "opacity-100"}`}
                    >
                      <CountrySelect
                        name="country"
                        disabled={isAllCustomer}
                        defaultValue={temporalCustomersList.country}
                        containerClassName=""
                        inputClassName=""
                        onChange={(country) =>
                          handleOnChangeAddress("country", country)
                        }
                        placeHolder="Select country"
                        style={{
                          width: "100%",
                          padding: "9px 12px",
                          border: "1.5px solid",
                          borderColor: "var(--tw-border-opacity, #E2E8F0)",
                          borderRadius: "10px",
                          fontSize: "14px",
                          outline: "none",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* ── Category & Status ── */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {/* Category */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                        {translate("category")}
                      </label>
                      <label
                        className={`flex items-center gap-1.5 transition-opacity ${isAllCustomer ? "opacity-40 cursor-default" : "cursor-pointer"}`}
                      >
                        <input
                          type="checkbox"
                          disabled={isAllCustomer}
                          checked={temporalCustomersList.category === "ALL"}
                          onChange={(e) =>
                            setTemporalCustomersList((prev) => ({
                              ...prev,
                              category: e.target.checked ? "ALL" : "",
                            }))
                          }
                          className="w-3.5 h-3.5 accent-blue-700"
                        />
                        <span className="text-xs text-slate-500 dark:text-gray-400 font-medium">
                          {translate("all")}
                        </span>
                      </label>
                    </div>
                    <select
                      id="category"
                      name="category"
                      disabled={
                        isAllCustomer ||
                        temporalCustomersList.category === "ALL"
                      }
                      value={
                        temporalCustomersList.category === "ALL"
                          ? ""
                          : temporalCustomersList.category
                      }
                      onChange={handleOnChange}
                      className={`w-full px-3 py-2 text-sm border-[1.5px] border-slate-200 dark:border-gray-700 rounded-xl outline-none bg-white dark:bg-gray-800 text-slate-800 dark:text-gray-100 focus:border-blue-700 dark:focus:border-blue-500 transition-all
                        ${isAllCustomer || temporalCustomersList.category === "ALL" ? "opacity-45" : "opacity-100"}`}
                    >
                      {categories.map((opt) => (
                        <option key={opt.key} value={opt._id}>
                          {opt.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block mb-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300">
                      {translate("status")}
                    </label>
                    <select
                      id="status"
                      name="status"
                      value={temporalCustomersList.status}
                      onChange={handleOnChange}
                      className="w-full px-3 py-2 text-sm border-[1.5px] border-slate-200 dark:border-gray-700 rounded-xl outline-none bg-white dark:bg-gray-800 text-slate-800 dark:text-gray-100 focus:border-blue-700 dark:focus:border-blue-500 transition-all"
                    >
                      <option value="ACTIVE">{translate("active")}</option>
                      <option value="DEACTIVATE">
                        {translate("deactivate")}
                      </option>
                    </select>
                  </div>
                </div>

                {/* ── Contact Channels ── */}
                <div className="mb-7">
                  <label className="block mb-2 text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Contact channels
                  </label>
                  <div className="flex gap-3">
                    {[
                      {
                        key: "withEmail",
                        icon: (
                          <svg
                            width="16"
                            height="16"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                        ),
                        label: translate("with-email"),
                      },
                      {
                        key: "withPhone",
                        icon: (
                          <svg
                            width="16"
                            height="16"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498A1 1 0 0121 15.72V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                            />
                          </svg>
                        ),
                        label: translate("with-phone"),
                      },
                    ].map(({ key, icon, label }) => {
                      const checked = temporalCustomersList[key];
                      return (
                        <div
                          key={key}
                          onClick={() =>
                            !isAllCustomer &&
                            setTemporalCustomersList((prev) => ({
                              ...prev,
                              [key]: !prev[key],
                            }))
                          }
                          className={`flex-1 flex items-center gap-2.5 px-3.5 py-3 rounded-xl border-[1.5px] select-none transition-all duration-200
                            ${isAllCustomer ? "opacity-50 cursor-default" : "cursor-pointer"}
                            ${
                              checked
                                ? "border-blue-700 bg-indigo-50/60 dark:border-blue-500 dark:bg-blue-900/20"
                                : "border-slate-200 bg-slate-50 dark:border-gray-700 dark:bg-gray-800"
                            }`}
                        >
                          {/* Channel icon */}
                          <span
                            className={
                              checked
                                ? "text-blue-700 dark:text-blue-400"
                                : "text-slate-400 dark:text-gray-500"
                            }
                          >
                            {icon}
                          </span>

                          <span
                            className={`text-[13px] font-medium ${checked ? "text-slate-800 dark:text-gray-100" : "text-slate-500 dark:text-gray-400"}`}
                          >
                            {label}
                          </span>

                          {/* Checkbox indicator */}
                          <div
                            className={`ml-auto w-[18px] h-[18px] rounded-md flex items-center justify-center border-2 transition-all duration-150 flex-shrink-0
                              ${
                                checked
                                  ? "border-blue-700 bg-blue-700 dark:border-blue-500 dark:bg-blue-600"
                                  : "border-slate-300 bg-transparent dark:border-gray-600"
                              }`}
                          >
                            {checked && (
                              <svg
                                width="10"
                                height="10"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="white"
                                strokeWidth={3}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Divider */}
                <div className="h-px bg-slate-100 dark:bg-gray-700/60 mb-5" />

                {/* ── Actions ── */}
                <div className="flex justify-end gap-2.5">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={isLoading}
                    className="px-5 py-2.5 rounded-xl border-[1.5px] border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-500 dark:text-gray-400 text-sm font-medium hover:bg-slate-50 dark:hover:bg-gray-700 hover:text-slate-800 dark:hover:text-gray-100 transition-all duration-150"
                  >
                    {translate("cancel")}
                  </button>

                  <SubmitButton
                    isLoading={isLoading}
                    textLoading="Saving..."
                    text={translate(buttonName || "create")}
                    style={{
                      padding: "10px 22px",
                      borderRadius: "10px",
                      background: "rgb(26 86 219)",
                      color: "#fff",
                      fontSize: "14px",
                      fontWeight: 600,
                      border: "none",
                      cursor: "pointer",
                      boxShadow: "0 4px 12px rgba(99,102,241,0.3)",
                    }}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CreateCustomerListModal;
